import { onMounted, onUnmounted, watch } from 'vue';
import { useSocketStore } from '../stores/socketStore';

/** At most one refetch per this many ms, however the change is heard. */
const THROTTLE_MS = 2000;

/**
 * Keep a page showing the schedule as it is now.
 *
 * Two ways a change is heard. The server announces every schedule change as
 * `agenda:changed` on the tenant's agenda room, which this joins — that is
 * what makes a covered Office Hours day appear on both portals at once. And a
 * tab that comes back into view refetches, so a missed message (a dropped
 * socket, a sleeping laptop) costs a glance rather than a stale page.
 *
 * @param {() => unknown} refetch  what the page does to load the schedule again
 */
export function useAgendaLive(refetch) {
  const socketStore = useSocketStore();
  const domain = window.store?.subdomain || 'default';
  let last = 0;
  let stopWatch = null;

  const refresh = () => {
    const now = Date.now();
    if (now - last < THROTTLE_MS) return;
    last = now;
    refetch();
  };

  const onChanged = (payload) => {
    if (payload?.domain && payload.domain !== domain) return;
    refresh();
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') refresh();
  };

  const subscribe = () => {
    socketStore.emit('agenda:subscribe', { domain });
    socketStore.off('agenda:changed', onChanged);
    socketStore.on('agenda:changed', onChanged);
  };

  onMounted(async () => {
    document.addEventListener('visibilitychange', onVisible);
    try {
      await socketStore.connect?.();
    } catch {
      // No socket: the page still refetches whenever it comes back into view.
    }
    if (socketStore.socket) {
      subscribe();
    } else {
      stopWatch = watch(() => socketStore.socket, (socket) => {
        if (socket) { subscribe(); stopWatch?.(); stopWatch = null; }
      });
    }
  });

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisible);
    stopWatch?.();
    socketStore.off('agenda:changed', onChanged);
    socketStore.emit('agenda:unsubscribe', { domain });
  });

  return { refresh };
}
